/**
 * Focus stage tree artwork — the "Skeleton" treatment.
 *
 * Path data is transcribed verbatim from `ADDENDUM-focus-stages.md` §4 in the
 * design handoff. Do not redraw or "tidy" these coordinates: the **scale ramp
 * is load-bearing**. Each tree deliberately occupies more of the shared
 * `0 0 48 56` frame than the last, which is what makes the ladder read as
 * growth before a student reads a word:
 *
 * | Stage    | Height | Width | Its story              |
 * |----------|--------|-------|------------------------|
 * | Hazel    | ~24    | 14    | small and low          |
 * | Rowan    | ~32    | 15    | taller, still slim     |
 * | Birch    | ~39    | 13    | tall and narrow        |
 * | Oak      | ~40    | 30    | breadth                |
 * | Sequoia  | ~49    | 26    | height + trunk mass    |
 *
 * Never normalise the trees to a common size. If Oak and Sequoia read as the
 * same size, the ladder has failed.
 *
 * All trees are rooted on the ground line at `y = 52` — they never float.
 */

/**
 * Which pass a skeleton stroke belongs to.
 *
 * `trunk` and `primary` survive the compact variant; `secondary` is dropped
 * below ~24px, where the full skeleton fills in and turns to mud.
 */
export type StrokeTier = "trunk" | "primary" | "secondary";

export interface SkeletonPath {
  d: string;
  tier: StrokeTier;
}

export interface Circle {
  cx: number;
  cy: number;
  r: number;
}

export interface Ellipse {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

export interface StageTree {
  /** Matches `FOCUS_STAGES[i].name` in `lib/focusCapacity.ts`. */
  name: string;
  /** Solid canopy carrying the mass, filled with --rl-green-500. */
  canopyCircles?: readonly Circle[];
  canopyEllipse?: Ellipse;
  canopyPaths?: readonly string[];
  /** Branch structure drawn straight through the canopy in --rl-green-800. */
  skeleton: readonly SkeletonPath[];
  /** Rowan's berries — the detail that makes it legible next to Hazel. */
  berries?: readonly Circle[];
}

export const STAGE_TREES: readonly StageTree[] = [
  {
    name: "Hazel",
    canopyCircles: [
      { cx: 19.5, cy: 38, r: 4.7 },
      { cx: 28.5, cy: 37.5, r: 4.7 },
      { cx: 24, cy: 33, r: 5.1 },
    ],
    skeleton: [
      { d: "M24 52V43", tier: "trunk" },
      { d: "M24 46L19.5 40", tier: "primary" },
      { d: "M24 45L28.5 39.5", tier: "primary" },
      { d: "M24 44V34", tier: "secondary" },
    ],
  },
  {
    name: "Rowan",
    canopyEllipse: { cx: 24, cy: 27, rx: 7.7, ry: 7.3 },
    skeleton: [
      { d: "M24 52V25", tier: "trunk" },
      { d: "M24 38L20 33.5", tier: "primary" },
      { d: "M24 36L28 32", tier: "primary" },
      { d: "M24 30L20.5 26", tier: "secondary" },
      { d: "M24 29L27.5 25.5", tier: "secondary" },
    ],
    berries: [
      { cx: 21, cy: 31.8, r: 1 },
      { cx: 27, cy: 31.8, r: 1 },
    ],
  },
  {
    name: "Birch",
    canopyEllipse: { cx: 24, cy: 25, rx: 6, ry: 11.6 },
    skeleton: [
      { d: "M24 52V15", tier: "trunk" },
      { d: "M24 19C21 19 19.6 21 19.1 23.4", tier: "primary" },
      { d: "M24 24C27 24 28.4 26 28.9 28.4", tier: "primary" },
      { d: "M24 29C21 29 19.6 31 19.1 33.4", tier: "secondary" },
      // The bark marks are Birch's signature but vanish when small.
      { d: "M21.8 40h1.6", tier: "secondary" },
      { d: "M24.8 44.5h1.6", tier: "secondary" },
    ],
  },
  {
    name: "Oak",
    canopyPaths: [
      "M9 24.5C9 17 15.5 12.5 24 12.5C32.5 12.5 39 17 39 24.5C39 29 34.5 31.5 30 30.5C27.5 32.8 20.5 32.8 18 30.5C13.5 31.5 9 29 9 24.5Z",
    ],
    skeleton: [
      { d: "M24 52V30", tier: "trunk" },
      { d: "M24 38L18 32.5", tier: "primary" },
      { d: "M24 36L30 30.5", tier: "primary" },
      { d: "M24 31V20", tier: "secondary" },
      { d: "M24 24.5L18.5 19.5", tier: "secondary" },
      { d: "M24 23.5L29.5 18.5", tier: "secondary" },
    ],
  },
  {
    name: "Sequoia",
    canopyPaths: [
      "M24 20L37 34H11Z",
      "M24 11L33.5 24H14.5Z",
      "M24 3.5L30.5 14H17.5Z",
    ],
    skeleton: [
      // Both uprights are `trunk`: the thick double-line trunk is Sequoia's
      // story alongside its height, so neither survives being dropped.
      { d: "M20.8 52V33", tier: "trunk" },
      { d: "M27.2 52V33", tier: "trunk" },
      { d: "M20.8 41h6.4", tier: "primary" },
      { d: "M24 34V7", tier: "secondary" },
    ],
  },
];

export const TREE_VIEW_BOX = "0 0 48 56";
